import { useEffect, useRef, useState } from 'react';

import { IUserListItemFragment } from 'graphql/types';
import { useSelection } from 'common/components/Table';
import { ITableSelectionProps } from 'common/types';

export interface IUseSelectedUsers {
    selectionProps: ITableSelectionProps<IUserListItemFragment>;
    persistedSelectedUsers: IUserListItemFragment[];
    onDeselectUser: (userId: string) => void;
    clearSelectedUsers: () => void;
    onAddSelectedUsers: (usersIds: string[]) => void;
}

interface IProps {
    alreadyAddedUsers: string[];
    page?: number; // Used as trigger for updating selected users
    users: IUserListItemFragment[];
}

/**
 * Hook for managing selected users
 * The selected users are updated in the page cycle
 * @param alreadyAddedUsers - The users that are already added to the list
 * @param page - The current page number
 * @param users - The users objects that are currently visible
 */
export const useSelectedUsers = ({
    alreadyAddedUsers,
    page,
    users,
}: IProps): IUseSelectedUsers => {
    const selectedUsersRef = useRef<IUserListItemFragment[]>([]);
    const [persistedSelectedUsers, setPersistedSelectedUsers] = useState<
        IUserListItemFragment[]
    >([]);

    // Filter out the already added users and the currently selected users
    const selectFilter = (user: IUserListItemFragment) => {
        const inAlreadyAddedUsers = alreadyAddedUsers?.some(
            (addedUser) => addedUser === user.id
        );
        const inSelectedUsers = persistedSelectedUsers?.some(
            (persistedSelectedUsers) => persistedSelectedUsers.id === user.id
        );

        return !inAlreadyAddedUsers && !inSelectedUsers;
    };

    const selectionProps = useSelection(users, selectFilter);
    const {
        selected: selectedUserIds,
        setSelected,
        onSelectAll,
    } = selectionProps;

    const handleDeselectUser = (userId: string) => {
        setSelected(selectedUserIds.filter((id) => id !== userId));

        setPersistedSelectedUsers(
            persistedSelectedUsers.filter((user) => user.id !== userId)
        );

        selectedUsersRef.current = selectedUsersRef.current.filter(
            (selectedUser) => selectedUser.id !== userId
        );
    };

    const handleAddSelectedUsers = (usersIds: string[]) => {
        setPersistedSelectedUsers([
            ...persistedSelectedUsers,
            ...(users?.filter((user) => usersIds.includes(user.id)) || []),
        ]);
    };

    useEffect(() => {
        if (!selectedUserIds.length) return;

        setPersistedSelectedUsers([
            ...persistedSelectedUsers,
            ...selectedUsersRef.current,
        ]);

        // Clear the selected users when the page is changed
        if (!!selectedUserIds?.length) onSelectAll();
    }, [page]);

    const clearSelectedUsers = () => {
        setPersistedSelectedUsers([]);

        selectedUsersRef.current = [];
    };

    useEffect(() => {
        // Set the users that are currently selected in the page cycle
        selectedUsersRef.current = selectedUserIds
            .map((id) => users?.find((user) => user.id === id))
            .filter(Boolean);
    }, [selectedUserIds]);

    return {
        selectionProps,
        persistedSelectedUsers,
        onDeselectUser: handleDeselectUser,
        clearSelectedUsers,
        onAddSelectedUsers: handleAddSelectedUsers,
    };
};
